import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { useParams, useLocation } from "react-router-dom";
import { Header } from "../../../components/header/header";
import { InvoiceModal } from "./invoicesModal";
import { AddClient } from "../landing/addClient";
import "./details.css";
import api from "../../../api";

const MONTH_NAMES = [
  "", "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

const formatINR = (value) => {
  if (value === null || value === undefined) return "-";
  return `₹${Number(value).toLocaleString("en-IN")}`;
};

const INTERACTION_MODE_OPTIONS = [
  { label: "In Person",          value: "In Person",          icon: "person"         },
  { label: "Audio Call",         value: "Audio Call",         icon: "call"           },
  { label: "Video Call",         value: "Video Call",         icon: "videocam"       },
  { label: "Email",              value: "Email",              icon: "email"          },
  { label: "Could Not Connect",  value: "Could Not Connect",  icon: "block"   },
];

const commTypeIcon = (type) => {
  if (!type) return "chat";
  const t = type.toLowerCase();
  if (t.includes("could not") || t.includes("missed")) return "block";
  if (t.includes("audio")) return "call";
  if (t.includes("video"))  return "videocam";
  if (t.includes("email"))  return "email";
  if (t.includes("person") || t.includes("visit")) return "person";
  return "chat";
};

export const Details = () => {
  const { partyId } = useParams();
  const location    = useLocation();
  const salesPersonId = localStorage.getItem("salesPersonId");

  const partyName  = location.state?.partyName  || "Client Details";
  // ── phone/email passed from Landing via navigate state ───────────────────
  const partyPhone = location.state?.partyPhone || null;
  const partyEmail = location.state?.partyEmail || null;

  const [apiResponse,     setApiResponse]     = useState(null);
  const [reconnectDate,   setReconnectDate]   = useState("");
  const [interactionMode, setInteractionMode] = useState("In Person");
  const [interactionNote, setInteractionNote] = useState("");
  const [submitting,      setSubmitting]      = useState(false);
  const [submitMsg,       setSubmitMsg]       = useState("");
  const [communications,  setCommunications]  = useState([]);
  const [commsLoading,    setCommsLoading]    = useState(true);
  const [showInvoices,    setShowInvoices]    = useState(false);
  const [showUpdateClient, setShowUpdateClient] = useState(false);
  const dateInputRef = useRef(null);

  // ── Fetch inner page data ────────────────────────────────────────────────
  useEffect(() => {
    const fetchDetails = async () => {
      try {
        const response = await api.get("/api/innerpage", {
          params: { SalespersonId: salesPersonId, PartyId: partyId },
        });
        console.log("Inner Page API Response:", response.data);
        setApiResponse(response.data);
        const reconnect = response.data?.data?.reconnectInfo?.ReconnectDate;
        if (reconnect) {
          setReconnectDate(new Date(reconnect).toISOString().split("T")[0]);
        }
      } catch (error) {
        console.error("Inner Page API Error:", error.response?.data || error.message);
      }
    };
    fetchDetails();
  }, [partyId]);

  // ── Fetch communication list ─────────────────────────────────────────────
  const fetchComms = async () => {
    try {
      setCommsLoading(true);
      const response = await api.get("/api/communication/list", {
        params: { PartyId: partyId },
      });
      const list = response.data?.data || response.data || [];
      setCommunications(Array.isArray(list) ? list : []);
    } catch (error) {
      console.error("Communication List Error:", error.response?.data || error.message);
      setCommunications([]);
    } finally {
      setCommsLoading(false);
    }
  };

  useEffect(() => {
    fetchComms();
  }, [partyId]);

  // ── Submit interaction ───────────────────────────────────────────────────
  const handleSubmitInteraction = async () => {
    if (!interactionNote.trim()) {
      setSubmitMsg("Please add interaction notes before submitting.");
      setTimeout(() => setSubmitMsg(""), 3000);
      return;
    }
    if (!reconnectDate) {
      setSubmitMsg("Please select a reconnect date.");
      setTimeout(() => setSubmitMsg(""), 3000);
      return;
    }
    setSubmitting(true);
    setSubmitMsg("");
    try {
      await api.post("/api/communication/addupdate", {
        CommType:      interactionMode,
        EmployeeId:    salesPersonId,
        PartyId:       Number(partyId),
        Note:          interactionNote,
        ReconnectDate: new Date(reconnectDate).toISOString(),
      });
      setSubmitMsg("Interaction saved successfully!");
      setInteractionNote("");
      await fetchComms();
    } catch (error) {
      console.error("Submit Interaction Error:", error.response?.data || error.message);
      setSubmitMsg("Failed to save interaction. Please try again.");
    } finally {
      setSubmitting(false);
      setTimeout(() => setSubmitMsg(""), 4000);
    }
  };

  // ── Export CSV ───────────────────────────────────────────────────────────
  const handleExportCSV = () => {
    const monthlySummary = apiResponse?.data?.monthlySummary || [];
    const headers = ["Month", "Quantity", "Sales Value", "Receipts", "Order Share"];
    const rows    = monthlySummary.map((row) => [
      `${MONTH_NAMES[row.Mn]} ${row.Yr}`,
      row.TotalQty, row.InvoiceAmt, row.ReceiptAmt, `${row["Order Share"]}%`,
    ]);
    const csv  = [headers, ...rows].map((r) => r.join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement("a");
    a.href = url;
    a.download = `${partyName}_sales_history.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (!apiResponse) return <div>Loading...</div>;

  const details        = apiResponse.data;
  const sixMonthSales  = details.sixMonthSales?.[0]   || {};
  const creditSummary  = details.creditSummary         || {};
  const monthlySummary = details.monthlySummary        || [];
  const invoiceList    = details.invoiceOutstanding    || [];

  const monthlyPotential = sixMonthSales["Potential Sale"]    || 0;
  const top6MonthSales   = sixMonthSales["Top 6 Month Sales"] || 0;
  const outstandingAmt   = creditSummary["Out Standing"]      || 0;
  const overdueAmt       = creditSummary["Over Due"]          || 0;
  const creditDays       = creditSummary["Cr. Days"]          || 0;

  return (
    <div className="detailsMain">
      <Header pageTitle={partyName} breadcrumb="Clients" />

      <div className="detailsContentMain">

        {/* ===== LEFT PANEL ===== */}
        {/* FIX 2: panel is a fixed grid shell — only the interaction list scrolls */}
        <div className="detailsLeftPanel">

          {/* ── Static top block ── */}
          <div className="detailsLeftStatic">

            {/* Client Name + Badge */}
            <div className="detailsClientNameRow">
              <div className="detailsClientNameLeft">
                <div className="detailsClientIcon">
                  <span className="material-symbols-outlined">store</span>
                </div>
                <p className="detailsClientName">{partyName}</p>
              </div>
              <div className="detailsClientNameRight">
                <span className="detailsActiveBadge">Active</span>
                <button
                  className="detailsUpdateClientBtn"
                  onClick={() => setShowUpdateClient(true)}
                >
                  <span className="material-symbols-outlined" style={{ fontSize: "1.6vh" }}>edit</span>
                  Update Client
                </button>
              </div>
            </div>

            {/* Action Buttons — wired to phone/email, simple labels */}
            <div className="detailsActionButtons">
              <button
                className="detailsCallBtn"
                onClick={() => {
                  if (partyPhone) window.location.href = `tel:${partyPhone}`;
                }}
                disabled={!partyPhone}
                title={partyPhone ? `Call ${partyPhone}` : "No phone number available"}
              >
                <span className="material-symbols-outlined" style={{ fontSize: "1.8vh" }}>call</span>
                Call Client
              </button>
              <button
                className="detailsEmailBtn"
                onClick={() => {
                  if (partyEmail) window.location.href = `mailto:${partyEmail}`;
                }}
                disabled={!partyEmail}
                title={partyEmail ? `Email ${partyEmail}` : "No email available"}
              >
                <span className="material-symbols-outlined" style={{ fontSize: "1.8vh" }}>mail</span>
                Send Email
              </button>
            </div>

            {/* Reconnect Date + Interaction Mode */}
            <div className="detailsReconnectRow">
              <div className="detailsFieldGroup">
                <p className="detailsFieldLabel">Reconnect Date</p>
                <div
                  className="detailsDateInputWrapper"
                  onClick={() => dateInputRef.current?.showPicker()}
                >
                  <span className="detailsDateDisplayText">
                    {reconnectDate
                      ? new Date(reconnectDate + "T00:00:00").toLocaleDateString("en-IN")
                      : "Select date..."}
                  </span>
                  <span className="material-symbols-outlined detailsDateCalIcon">calendar_today</span>
                  <input
                    ref={dateInputRef}
                    type="date"
                    className="detailsNativeDateInput"
                    value={reconnectDate}
                    onChange={(e) => setReconnectDate(e.target.value)}
                  />
                </div>
              </div>

              <div className="detailsFieldGroup">
                <p className="detailsFieldLabel">Interaction Mode</p>
                <div className="detailsSelectWrapper">
                  <select
                    className="detailsSelect"
                    value={interactionMode}
                    onChange={(e) => setInteractionMode(e.target.value)}
                  >
                    {INTERACTION_MODE_OPTIONS.map((opt) => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                  <span className="material-symbols-outlined detailsSelectArrow">expand_more</span>
                </div>
              </div>
            </div>

            {/* Interaction Notes */}
            <div>
              <p className="detailsNotesLabel">Interaction Notes</p>
              <textarea
                className="detailsNotesTextarea"
                placeholder="Add detailed notes about your call, action items, or next steps here..."
                value={interactionNote}
                onChange={(e) => setInteractionNote(e.target.value)}
              />
            </div>

            {submitMsg && (
              <p className={`detailsSubmitMsg ${submitMsg.includes("success") ? "detailsSubmitMsgSuccess" : "detailsSubmitMsgError"}`}>
                {submitMsg}
              </p>
            )}

            {/* Submit */}
            <button className="detailsSubmitBtn" onClick={handleSubmitInteraction} disabled={submitting}>
              <span className="material-symbols-outlined">{submitting ? "hourglass_top" : "send"}</span>
              {submitting ? "Submitting..." : "Submit Interaction"}
            </button>

            {/* Recent Interactions header — stays visible */}
            <div className="detailsRecentHeader">
              <p className="detailsRecentTitle">Recent Interactions</p>
            </div>
          </div>

          {/* ── FIX 2: ONLY this div scrolls ── */}
          <div className="detailsInteractionList">
            {commsLoading ? (
              <p className="detailsCommsLoading">Loading interactions...</p>
            ) : communications.length === 0 ? (
              <p className="detailsCommsEmpty">No interactions recorded yet.</p>
            ) : (
              communications.map((item, idx) => (
                <div className="detailsInteractionItem" key={idx}>
                  <div className="detailsInteractionIcon">
                    <span className="material-symbols-outlined">{commTypeIcon(item.TrnCommType)}</span>
                  </div>
                  <div className="detailsInteractionContent">
                    <div className="detailsInteractionMeta">
                      <span className="detailsInteractionDate">
                        {item.TrnCommEntryDate
                          ? new Date(item.TrnCommEntryDate).toLocaleDateString("en-IN", {
                              day: "2-digit", month: "short", year: "numeric",
                            })
                          : item.TrnCommReconnectDate
                          ? new Date(item.TrnCommReconnectDate).toLocaleDateString("en-IN", {
                              day: "2-digit", month: "short", year: "numeric",
                            })
                          : "-"}
                      </span>
                      <span className="detailsInteractionSep">|</span>
                      <span className="detailsInteractionType">{item.TrnCommType || "Note"}</span>
                    </div>
                    <p className="detailsInteractionNote">{item.TrnCommNote}</p>
                    {item.TrnCommReconnectDate && (
                      <p className="detailsInteractionReconnect">
                        <span className="material-symbols-outlined" style={{ fontSize: "1.2vh", verticalAlign: "middle" }}>event</span>
                        {" "}Reconnect: {new Date(item.TrnCommReconnectDate).toLocaleDateString("en-IN")}
                      </p>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>

        </div>

        {/* ===== RIGHT PANEL ===== */}
        <div className="detailsRightPanel">

          {/* Top Cards Row */}
          <div className="detailsTopCards">

            {/* Monthly Potential Card */}
            <div className="detailsPotentialCard">
              <div className="detailsPotentialHeader">
                <p className="detailsPotentialTitle">Monthly Potential</p>
                <div className="detailsPotentialIcon">
                  <span className="material-symbols-outlined">trending_up</span>
                </div>
              </div>
              <div>
                <p className="detailsPotentialAmount">{formatINR(monthlyPotential)}</p>
                <p className="detailsPotentialSubtitle">Estimated monthly sales</p>
              </div>
              <div className="detailsPotentialFooter">
                <p className="detailsPotentialFooterLabel">Top 6 Month Sales</p>
                <p className="detailsPotentialFooterValue">{formatINR(top6MonthSales)}</p>
              </div>
            </div>

            {/* Outstanding Balance Card */}
            <div className="detailsOutstandingCard">
              <div className="detailsOutstandingHeader">
                <p className="detailsOutstandingTitle">Outstanding Balance</p>
                <button className="detailsViewInvoicesBtn" onClick={() => setShowInvoices(true)}>
                  <span className="material-symbols-outlined">open_in_new</span>
                  View Invoices
                </button>
              </div>
              <div>
                <p className="detailsOutstandingAmount">{formatINR(overdueAmt)}</p>
                <p className="detailsOverdueLabel">Current Overdue Amount</p>
              </div>
              <hr className="detailsOutstandingDivider" />
              <div className="detailsOutstandingRow">
                <p className="detailsOutstandingRowLabel">Credit Days</p>
                <p className="detailsOutstandingRowValue">{creditDays}</p>
              </div>
              <hr className="detailsOutstandingDivider" />
              <div className="detailsOutstandingRow">
                <p className="detailsOutstandingRowLabel">Due + Undue Total</p>
                <p className="detailsOutstandingRowValue">{formatINR(outstandingAmt)}</p>
              </div>
            </div>
          </div>

          {/* Sales & Receipts History Table */}
          <div className="detailsSalesHistoryCard">
            <div className="detailsSalesHistoryHeader">
              <p className="detailsSalesHistoryTitle">Sales &amp; Receipts History</p>
              <button className="detailsExportBtn" onClick={handleExportCSV}>
                <span className="material-symbols-outlined">download</span>
                Export CSV
              </button>
            </div>
            <div className="detailsSalesTableWrapper">
              <table className="detailsSalesTable">
                <thead>
                  <tr>
                    <th>MONTH</th>
                    <th>QUANTITY</th>
                    <th>SALES VALUE</th>
                    <th>RECEIPTS</th>
                    <th>ORDER SHARE</th>
                  </tr>
                </thead>
                <tbody>
                  {monthlySummary.map((row, idx) => {
                    const hasReceipt = row.ReceiptAmt > 0;
                    return (
                      <tr key={idx}>
                        <td>{MONTH_NAMES[row.Mn]} {row.Yr}</td>
                        <td>{row.TotalQty ? row.TotalQty.toLocaleString("en-IN") : "0"}</td>
                        <td>{formatINR(row.InvoiceAmt)}</td>
                        <td>
                          <span className={hasReceipt ? "detailsReceiptGreen" : "detailsReceiptZero"}>
                            {formatINR(row.ReceiptAmt)}
                          </span>
                        </td>
                        <td>{row["Order Share"] ? `${row["Order Share"].toFixed(2)}%` : "0.00%"}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

      {/* Invoice Modal */}
      {showInvoices && (
        <InvoiceModal
          invoices={invoiceList}
          partyName={partyName}
          partyId={partyId}
          onClose={() => setShowInvoices(false)}
        />
      )}

      {/* Update Client Modal */}
      {showUpdateClient && (
        <AddClient
          mode="update"
          partyId={Number(partyId)}
          onClose={() => setShowUpdateClient(false)}
        />
      )}
    </div>
  );
};